import { PropsWithChildren, ReactElement } from 'react';

import clsx from 'clsx';

import styles from './badge.module.scss';

enum BadgeVariant {
  GREY = 'grey',
  RED = 'red',
  PURPLE = 'purple',
  GREEN = 'green',
  YELLOW = 'yellow',
}

const BadgeType = {
  ULTRA_LIMITED: 'ultra_limited',
  LUMINO: 'lumino',
  LIMITED: 'limited',
  PROMO: 'promo',
  SALE: 'sale',
  BRAND_PREMIERE: 'brand_premiere',
  EXCLUSIVE_DROP: 'exclusive_drop',
  NEW_COLLECTION: 'new_collection',
  CHARITY: 'charity',
  ACCESSORIES: 'accessories',
  INSPIRATIONS: 'inspirations',
  ARTISTS: 'artists',
  COMMUNITY: 'community',
  EPIC_GIFT: 'epic_gift',
  LAST_CHANCE: 'last_chance',
  LEAVING_SOON: 'leaving_soon',
} as const;

const BadgeBase = ({
  variant,
  children,
}: PropsWithChildren<{ variant: BadgeVariant }>): ReactElement => (
  <span className={clsx(styles.badge, styles[variant])} data-testid="badge">
    {children}
  </span>
);

const UltraLimitedBadge = () => (
  <BadgeBase variant={BadgeVariant.GREY}>ultra limited edition</BadgeBase>
);
const LuminoBadge = () => <BadgeBase variant={BadgeVariant.GREY}>lumino</BadgeBase>;
const LimitedBadge = () => <BadgeBase variant={BadgeVariant.GREY}>limited edition</BadgeBase>;
const PromoBadge = () => <BadgeBase variant={BadgeVariant.RED}>promo</BadgeBase>;
const SaleBadge = () => <BadgeBase variant={BadgeVariant.RED}>on sale</BadgeBase>;
const BrandPremiereBadge = () => (
  <BadgeBase variant={BadgeVariant.PURPLE}>brand premiere</BadgeBase>
);
const ExclusiveDropBadge = () => (
  <BadgeBase variant={BadgeVariant.PURPLE}>exclusive drop</BadgeBase>
);
const NewCollectionBadge = () => <BadgeBase variant={BadgeVariant.GREEN}>new collection</BadgeBase>;
const CharityBadge = () => <BadgeBase variant={BadgeVariant.GREEN}>charity</BadgeBase>;
const AccessoriesBadge = () => <BadgeBase variant={BadgeVariant.YELLOW}>accessories</BadgeBase>;
const InspirationsBadge = () => <BadgeBase variant={BadgeVariant.YELLOW}>inspirations</BadgeBase>;
const ArtistsBadge = () => <BadgeBase variant={BadgeVariant.YELLOW}>artists</BadgeBase>;
const CommunityBadge = () => <BadgeBase variant={BadgeVariant.YELLOW}>community</BadgeBase>;
const EpicGiftBadge = () => <BadgeBase variant={BadgeVariant.RED}>epic gift</BadgeBase>;
const LastChanceBadge = () => <BadgeBase variant={BadgeVariant.RED}>last chance</BadgeBase>;
const LeavingSoonBadge = () => <BadgeBase variant={BadgeVariant.RED}>leaving soon</BadgeBase>;

interface Props {
  type: (typeof BadgeType)[keyof typeof BadgeType];
}

const Badge = ({ type }: Props): ReactElement | null => {
  switch (type) {
    case BadgeType.ULTRA_LIMITED:
      return <UltraLimitedBadge />;
    case BadgeType.LUMINO:
      return <LuminoBadge />;
    case BadgeType.LIMITED:
      return <LimitedBadge />;

    case BadgeType.PROMO:
      return <PromoBadge />;
    case BadgeType.SALE:
      return <SaleBadge />;

    case BadgeType.BRAND_PREMIERE:
      return <BrandPremiereBadge />;
    case BadgeType.EXCLUSIVE_DROP:
      return <ExclusiveDropBadge />;

    case BadgeType.NEW_COLLECTION:
      return <NewCollectionBadge />;
    case BadgeType.CHARITY:
      return <CharityBadge />;

    case BadgeType.ACCESSORIES:
      return <AccessoriesBadge />;
    case BadgeType.INSPIRATIONS:
      return <InspirationsBadge />;
    case BadgeType.ARTISTS:
      return <ArtistsBadge />;
    case BadgeType.COMMUNITY:
      return <CommunityBadge />;
    case BadgeType.EPIC_GIFT:
      return <EpicGiftBadge />;
    case BadgeType.LAST_CHANCE:
      return <LastChanceBadge />;
    case BadgeType.LEAVING_SOON:
      return <LeavingSoonBadge />;
    default:
      return null;
  }
};

export default Badge;
